import { Feature, Features } from "../feature";

/**
 * PIPH-214: When a ketone's carbonyl carbon sits between two rings (or other
 * primaries) but belongs to neither, it is structurally a 1-carbon chain that
 * connects them, with the =O hanging off as a Pattern A substituent. The =O is
 * a dead end — it doesn't form any path between the rings.
 *
 * The chain extractor already produces a `chain.1` candidate `[C]` for the
 * bridging carbon, but `chain1Filter` would later delete it because the ketone
 * fg's `atoms = [C, O]` "contains" the chain's atom. This filter shrinks the
 * ketone fg's atoms to `[O]` only, so:
 *  - chain1Filter no longer sees a containing feature → keeps the chain.1
 *  - subFeatureExtractor.findBestParent assigns the ketone (now atoms=[O],
 *    connections=[C]) to the chain.1 via the connection-overlap branch.
 *
 * Runs AFTER looseAtomsFilter and BEFORE chain1Filter in the pipeline.
 */
export const bridgingCarbonylPromoter = {
    filterInPlace: (features: Feature[]) => {
        const ketones = features.filter(f => f.name === Features.ketone);
        if (ketones.length === 0) return;

        const primaries = features.filter(isPrimaryFeature);

        for (const ketone of ketones) {
            // carbonylCoreExtractor builds atoms = [C, O]; carbonylExtractor sets
            // connections = [C]. If anything has reshaped this, bail out.
            if (ketone.atoms.length !== 2) continue;
            if (ketone.connections.length !== 1) continue;
            const carbonylC = ketone.atoms[0];
            if (ketone.connections[0] !== carbonylC) continue;

            // Must be a chain.1 with exactly this carbon.
            const chain1 = features.find(f =>
                f.atoms.length === 1 &&
                f.atoms[0] === carbonylC &&
                f.name === `${Features.chain}.1`,
            );
            if (!chain1) continue;

            // The carbon must bridge ≥ 2 primaries (not counting itself).
            const ringsBondedToC = primaries.filter(p =>
                p !== chain1 && p.connections.includes(carbonylC),
            );
            if (ringsBondedToC.length < 2) continue;

            // Promote: trim C out of the ketone's atoms. =O is the only atom left.
            // The chain1 will now survive chain1Filter, and the ketone will be
            // reparented to chain1 by subFeatureExtractor.findBestParent.
            ketone.atoms = ketone.atoms.filter(a => a !== carbonylC);
        }
    },
};

function isPrimaryFeature(f: Feature): boolean {
    return (
        f.name.startsWith(Features.ring) ||
        f.name.startsWith(Features.chain) ||
        f.name.startsWith(Features.ringSystem) ||
        f.name.startsWith(Features.bridgedRing) ||
        f.name === Features.atomCluster
    );
}
